import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { ErrorType } from '@constants/error.constant';
import { PeriodicityEnum } from '@enums/periodicity.enum';
import { ContractOffer } from '@gi/models/contract-offer';
import { Prestation } from '@gi/models/prestation';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { TimeTrackingsTask } from '@models/time-trackings-task';

function discountValidator(): ValidatorFn {
  return (prestationForm: PrestationForm): ValidationErrors => {
    if (prestationForm.hasDiscount) {
      if (!prestationForm.discountPrintedLabel.value) {
        FormHelper.addErrorToControl(prestationForm.discountPrintedLabel, ErrorType.REQUIRED);
      }
      if (!prestationForm.discountAmount.value) {
        FormHelper.addErrorToControl(prestationForm.discountAmount, ErrorType.REQUIRED);
      }
    } else {
      FormHelper.removeErrorFromControl(prestationForm.discountPrintedLabel, ErrorType.REQUIRED);
      FormHelper.removeErrorFromControl(prestationForm.discountAmount, ErrorType.REQUIRED);
    }

    return null;
  };
}

export type PrestationFormType = {
  id: FormControl<number | undefined>;
  code: FormControl<string | null>;
  label: FormControl<string | null>;
  amountExcludedTax: FormControl<number | null>;
  periodicity: FormControl<PeriodicityEnum | null>;
  task: FormControl<TimeTrackingsTask | null>;
  contractOffer: FormControl<ContractOffer | null>;
  discountPrintedLabel: FormControl<string | null>;
  discountAmount: FormControl<number | null>;
  discountOccurrence: FormControl<number | null>;
};

export class PrestationForm extends FormGroup<PrestationFormType> {
  get id(): FormControl<number | undefined> {
    return this.controls.id;
  }

  get code(): FormControl<string | null> {
    return this.controls.code;
  }

  get label(): FormControl<string | null> {
    return this.controls.label;
  }

  get amountExcludedTax(): FormControl<number | null> {
    return this.controls.amountExcludedTax;
  }

  get amountExcludedTaxErrorMessage(): ErrorMessage | null {
    return FormHelper.composeErrorMessages(this.amountExcludedTax, [
      {
        method: 'getEmptyErrorMessage',
        args: ['Le montant HT est requis']
      },
      {
        method: 'getMinErrorMessage',
        args: ['Le montant HT doit être supérieur à 0']
      }
    ]);
  }

  get periodicity(): FormControl<PeriodicityEnum | null> {
    return this.controls.periodicity;
  }

  get periodicityErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.controls.periodicity, 'La fréquence de facturation est requise');
  }

  get task(): FormControl<TimeTrackingsTask | null> {
    return this.controls.task;
  }

  get contractOffer(): FormControl<ContractOffer | null> {
    return this.controls.contractOffer;
  }

  get discountPrintedLabel(): FormControl<string | null> {
    return this.controls.discountPrintedLabel;
  }

  get discountAmount(): FormControl<number | null> {
    return this.controls.discountAmount;
  }

  get discountAmountErrorMessage(): ErrorMessage | null {
    return FormHelper.getMinErrorMessage(this.discountAmount, 'Le montant de la remise doit être supérieur à 0');
  }

  get discountOccurrence(): FormControl<number | null> {
    return this.controls.discountOccurrence;
  }

  get hasDiscount(): boolean {
    return !!this.discountPrintedLabel.value || !!this.discountAmount.value || !!this.discountOccurrence.value;
  }

  constructor() {
    super(
      {
        id: new FormControl<number | undefined>(undefined),
        code: new FormControl<string | null>(null),
        label: new FormControl<string | null>(null, Validators.required),
        amountExcludedTax: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
        periodicity: new FormControl<PeriodicityEnum | null>(null, Validators.required),
        task: new FormControl<TimeTrackingsTask | null>(null, Validators.required),
        contractOffer: new FormControl<ContractOffer | null>(null),
        discountPrintedLabel: new FormControl<string | null>(null),
        discountAmount: new FormControl<number | null>(null, Validators.min(0.01)),
        discountOccurrence: new FormControl<number | null>(null)
      },
      discountValidator()
    );
  }

  fromPrestation(prestation: Prestation): void {
    this.patchValue({
      id: prestation.id,
      code: prestation.code,
      label: prestation.label,
      amountExcludedTax: prestation.amountExcludedTax,
      periodicity: prestation.periodicity,
      task: prestation.task,
      contractOffer: prestation.contractOffer,
      discountPrintedLabel: prestation.discountPrintedLabel,
      discountAmount: prestation.discountAmount,
      discountOccurrence: prestation.discountOccurrence
    });

    if (prestation.contractOffer?.offer?.id) {
      this.disableTask();
    }
  }

  toPrestation(): Prestation {
    return new Prestation(
      this.id.value,
      this.code.value,
      this.label.value,
      this.amountExcludedTax.value,
      this.periodicity.value,
      this.task.value,
      this.contractOffer.value,
      this.discountPrintedLabel.value,
      this.discountAmount.value,
      this.discountOccurrence.value
    );
  }

  onTaskChange(): void {
    if (this.task.value?.wording) {
      this.label.setValue(this.task.value.wording);
    }

    if (this.task.value?.code) {
      this.code.setValue(this.task.value.code);
    }
  }

  disableTask(): void {
    this.task.disable();
  }

  addDiscount(): void {
    const label = `Remise${this.label.value ? ` sur ${this.label.value}` : ''}`;
    this.discountPrintedLabel.setValue(label);
  }

  removeDiscount(): void {
    this.patchValue({
      discountPrintedLabel: null,
      discountAmount: null,
      discountOccurrence: null
    });
  }
}
